import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Card, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import { FaInfoCircle, FaPlus } from "react-icons/fa";
import { debounce } from "lodash";
import { useParams, useNavigate } from "react-router-dom";
import { getHostelFacilities } from "../../../services/OrganisationServices/hostelManagmentService";
import { addRoomFacility } from "../../../services/OrganisationServices/hostelRoomServices";
import {
  callToast,
  showErrorMessages,
} from "../../../services/utilityFunctions";
import { useGlobalState } from "../../../services/hostelConst";

const AddRoomFacilityComponent = ({ data, refresh, navigate }) => {
  console.log("AddRoomFacilityComponent props:", { data, refresh, navigate });

  const { closeModal } = useGlobalState();

  const hostelId = data?._HOSTEL;
  const roomId = data?._ID;

  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [initialFacilities, setInitialFacilities] = useState([]);

  useEffect(() => {
    fetchInitialFacilities();
  }, []);

  const fetchInitialFacilities = async () => {
    try {
      setFacilitiesLoading(true);
      const response = await getHostelFacilities({
        hostelId,
        pageNum: 1,
        query: "",
      });

      console.log("Initial facilities response:", response);

      if (response.status === "success") {
        const facilityList = response.data || [];
        console.log("Facility List:", facilityList);

        if (Array.isArray(facilityList)) {
          const options = facilityList.map((facility) => ({
            value: facility._ID,
            label: facility.NAME,
            data: {
              name: facility.NAME,
              description: facility.DESCRIPTION,
              scope: facility.SCOPE,
              createdDatetime: facility["CREATED ON"],
              ...facility,
            },
          }));
          console.log("Formatted options:", options);
          setInitialFacilities(options);
        }
      }
    } catch (error) {
      console.error("Error loading initial facilities:", error);
      const myButton = document.getElementById("myToastButton");
      callToast(
        "error",
        "Error",
        "Failed to load initial facilities",
        myButton
      );
      setInitialFacilities([]);
    } finally {
      setFacilitiesLoading(false);
    }
  };

  const loadFacilities = useCallback(
    debounce(async (inputValue, callback) => {
      try {
        setFacilitiesLoading(true);
        const response = await getHostelFacilities({
          hostelId,
          pageNum: 1,
          query: inputValue || "",
        });

        console.log("Search facilities response:", response);

        if (response.status === "success") {
          const facilityList = response.data || [];
          const options = facilityList.map((facility) => ({
            value: facility._ID,
            label: facility.NAME,
            data: {
              name: facility.NAME,
              description: facility.DESCRIPTION,
              scope: facility.SCOPE,
              createdDatetime: facility["CREATED ON"],
              ...facility,
            },
          }));
          console.log("Formatted search options:", options);
          callback(options);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error("Error loading facilities:", error);
        callback([]);
      } finally {
        setFacilitiesLoading(false);
      }
    }, 300),
    [hostelId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFacility) {
      setErrors({ facility: "Please select a facility" });
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        hostelId,
        roomId,
        facilityId: selectedFacility.value,
      };

      console.log("Submitting payload:", payload);
      const response = await addRoomFacility(payload);

      if (response.status === "success") {
        const myButton = document.getElementById("myToastButton");
        callToast(
          "success",
          "Success",
          "Facility added successfully",
          myButton
        );

        if (typeof refresh === "function") {
          await refresh();
        }

        closeModal();

        setSelectedFacility(null);
      } else {
        showErrorMessages({ data: response, navigate });
      }
    } catch (error) {
      console.error("Error adding facility:", error);
      const myButton = document.getElementById("myToastButton");
      callToast("error", "Error", "Failed to add facility", myButton);

      showErrorMessages({
        data: {
          message: "Failed to add facility",
          errors: error.message,
        },
        navigate,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <Card.Body className="px-4 py-4">
        <h5 className="mb-4">Add Room Facility</h5>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Select Facility</Form.Label>
                <Select
                  cacheOptions
                  options={initialFacilities}
                  defaultOptions={initialFacilities}
                  value={selectedFacility}
                  loadOptions={loadFacilities}
                  onChange={(selected) => {
                    setSelectedFacility(selected);
                    setErrors({ ...errors, facility: null });
                  }}
                  isLoading={facilitiesLoading}
                  isSearchable
                  placeholder="Search facilities..."
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? "No matching facilities"
                      : "Start typing to search"
                  }
                  className={errors.facility ? "is-invalid" : ""}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: errors.facility
                        ? "#dc3545"
                        : base.borderColor,
                      "&:hover": {
                        borderColor: errors.facility
                          ? "#dc3545"
                          : base.borderColor,
                      },
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      position: "absolute",
                      width: "100%",
                    }),
                  }}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                />
                {errors.facility && (
                  <div className="invalid-feedback d-block">
                    {errors.facility}
                  </div>
                )}
              </Form.Group>
            </Col>

            {selectedFacility && (
              <Col md={12}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={12}>
                        <h5 className="mb-3">
                          <FaInfoCircle className="me-2 text-primary" />
                          Selected Facility Details
                        </h5>
                      </Col>
                      <Col md={6}>
                        <dl className="row mb-0">
                          <dt className="col-sm-4">Name:</dt>
                          <dd className="col-sm-8">
                            {selectedFacility.data.name}
                          </dd>

                          <dt className="col-sm-4">Description</dt>
                          <dd className="col-sm-8">
                            {selectedFacility.data.description || "N/A"}
                          </dd>
                        </dl>
                      </Col>
                      <Col md={6}>
                        <dl className="row mb-0">
                          <dt className="col-sm-4">Scope:</dt>
                          <dd className="col-sm-8 text-capitalize">
                            {selectedFacility.data.scope?.toLowerCase() ||
                              "N/A"}
                          </dd>

                          <dt className="col-sm-4">Created On:</dt>
                          <dd className="col-sm-8">
                            {selectedFacility.data.createdDatetime || "N/A"}
                          </dd>
                        </dl>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="light" onClick={closeModal} className="px-4">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={!selectedFacility || submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Adding...
                </>
              ) : (
                <>
                  <FaPlus className="me-2" />
                  Submit
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
  );
};

export default AddRoomFacilityComponent;
